"use client";

import { type FC, useCallback, useEffect, useMemo, useState } from "react";
import { trackObCarouselView } from "@/src/hooks/onboarding/onboardingAnalytics";
import { cn } from "utils";
import { OnboardingCarousel } from "../OnboardingCarousel";
import { IntroActiveIndexProvider } from "./IntroActiveIndexContext";
import { IntroGetStartedCta, IntroSlideShell } from "./IntroSlideShell";
import { IntroHeadline } from "./IntroHeadline";
import { SIGN_IN_INTRO_CAROUSEL_INTERVAL_MS } from "./introSlidesConfig";
import { createSignInIntroCopy } from "./signInIntroCopy";
import type { SignInIntroPhaseProps } from "./types";
import { IntroSlide1Hero } from "./slides/IntroSlide1Hero";
import { IntroSlide2Radar } from "./slides/IntroSlide2Radar";
import { IntroSlide3Goals } from "./slides/IntroSlide3Goals";
import { IntroSlide4Activity } from "./slides/IntroSlide4Activity";
import { useParams } from "next/navigation";

export const SignInIntroPhase: FC<SignInIntroPhaseProps> = ({
  onGetStarted,
  className,
  initialSlideIndex = 0,
  freezeCarousel = false,
}) => {
  const { locale = "en" } = useParams() as { locale?: string };
  const [activeIndex, setActiveIndex] = useState(initialSlideIndex);
  const copy = useMemo(() => createSignInIntroCopy(locale), [locale]);

  useEffect(() => {
    trackObCarouselView();
  }, []);

  const handleChange = useCallback((index: number) => {
    setActiveIndex(index);
  }, []);

  const items = useMemo(() => {
    return [
      {
        id: "intro-1",
        content: (
          <IntroSlideShell
            slideIndex={0}
            headline={
              <IntroHeadline
                prefix={copy.intro1.headlinePrefix}
                accent={copy.intro1.headlineAccent}
                suffix={copy.intro1.headlineSuffix}
              />
            }
            subtitle={copy.intro1.subtitle}
          >
            <IntroSlide1Hero />
          </IntroSlideShell>
        ),
      },
      {
        id: "intro-2",
        content: (
          <IntroSlideShell
            slideIndex={1}
            hideDesktopHero
            desktopVisual={<IntroSlide2Radar layout="desktop" />}
            headline={
              <IntroHeadline
                prefix={copy.intro2.headlinePrefix}
                accent={copy.intro2.headlineAccent}
                suffix={copy.intro2.headlineSuffix}
              />
            }
            subtitle={copy.intro2.subtitle}
          >
            <IntroSlide2Radar layout="mobile" />
          </IntroSlideShell>
        ),
      },
      {
        id: "intro-3",
        content: (
          <IntroSlideShell
            slideIndex={2}
            hideDesktopHero
            desktopVisual={<IntroSlide3Goals layout="desktop" />}
            headline={<IntroHeadline text={copy.intro3.headline} />}
            subtitle={copy.intro3.subtitle}
          >
            <IntroSlide3Goals layout="mobile" />
          </IntroSlideShell>
        ),
      },
      {
        id: "intro-4",
        content: (
          <IntroSlideShell
            slideIndex={3}
            hideDesktopHero
            desktopVisual={<IntroSlide4Activity layout="desktop" />}
            headline={<IntroHeadline text={copy.intro4.headline} />}
            subtitle={copy.intro4.subtitle}
          >
            <IntroSlide4Activity layout="mobile" />
          </IntroSlideShell>
        ),
      },
    ];
  }, [copy]);

  return (
    <IntroActiveIndexProvider value={activeIndex}>
      <div
        className={cn("relative h-dvh w-full overflow-hidden", className)}
        data-testid="sign-in-intro-phase"
      >
        <OnboardingCarousel
          items={items}
          autoplay={!freezeCarousel}
          interval={SIGN_IN_INTRO_CAROUSEL_INTERVAL_MS}
          initialIndex={initialSlideIndex}
          showIndicator={false}
          className="h-full"
          onChange={handleChange}
        />
        <IntroGetStartedCta label={copy.ctaGetStarted} onClick={onGetStarted} />
      </div>
    </IntroActiveIndexProvider>
  );
};
